// UnsyncedCaptureBanner — persistent, honest "N changes not yet saved"
// indicator for the field-capture chrome.
//
// WHY (the capture-data-loss incident, containment). Counts are written
// local-first and uploaded asynchronously; when the upload queue JAMS (a
// non-permanent error rethrows so the whole ps_crud batch retries forever) or a
// count is quarantined, the work sits on-device unconfirmed while the app
// historically implied success. The existing DroppedChangeToast only fires on
// drop/quarantine EVENTS — it cannot see a jammed/pending queue, which emits no
// event. This banner closes that gap: it reflects the LIVE held-count
// (pending upload queue + quarantine) from the shared held-work context, so a
// surveyor is never told "done" while work is still unsynced.
//
// The noun is changes, not items: the count is the sum of the ps_crud upload
// queue + the orphaned_capture_items quarantine (heldCaptureTotal). The queue
// dimension holds writes for every client-writable table — room renames and
// session completes as well as capture counts — so an item-counting noun would
// mislabel it. This is the SAME quantity the logout dialog warns about, worded
// identically via the shared heldChangeNoun (audit PR #732).
//
// Copy is sync-NEUTRAL ("not yet saved on the server"), NOT a remediation
// instruction. The total folds in quarantined counts (orphaned_capture_items),
// which never "sync" by staying online — they recover out-of-band via the
// auto-rescue inbox. A prescriptive "keep online until they sync" tail would be
// false for the quarantine dimension AND would contradict the sibling
// DroppedChangeToast ("held safely … recover them"). The banner owes the honest
// STATE; per-dimension remediation is owned elsewhere (audit PR #732 / M4).
//
// Ambient + self-clearing: role="status" (a polite live region — steady-state
// information, not a one-off error) and NO dismiss — it shows whenever the
// device holds unconfirmed work and disappears the moment the queue drains. It
// renders nothing at count 0 or when the device can't be read (count null) — we
// never show a false zero, and the destructive logout path (not this ambient
// hint) is what fails closed. The two surfaces share the SUM and the NOUN; they
// intentionally differ only on the indeterminate read (dialog warns, banner
// hides).
//
// Placement: rendered as the TOP ROW of CaptureLayout's sticky header (M1+M2),
// so it stays pinned while the surveyor scrolls a capture screen. Its count
// comes from the route-level UnsyncedCaptureProvider via context — it does NOT
// call the probe hook itself, so the probe machine is mounted once and outlives
// per-screen navigations (audit PR #732 / #6). The sticky header owns the top
// safe-area inset (pt-safe); this row sits inside it and must NOT re-apply it.

import type { ReactElement } from 'react'

import { CloudOff } from 'lucide-react'

import { heldChangeNoun } from '@/hooks/powersync/logout-cleanup'

import { useUnsyncedCaptureContext } from './unsynced-capture-provider'

export function UnsyncedCaptureBanner(): ReactElement | null {
  const { count } = useUnsyncedCaptureContext()
  // Render nothing unless we have a positive, finite held count. The explicit
  // `!(count > 0)` (not `count <= 0`) also rejects a non-finite count
  // (`NaN > 0` is false): device-unreadable is `null`, and any sentinel that
  // ever leaked through must hide, never render "NaN changes" (#14).
  if (count === null || !(count > 0)) return null
  const noun = heldChangeNoun(count)
  // No pt-safe here: this row sits INSIDE the sticky header, which already
  // carries the top safe-area inset (M2 — a single inset owner). border-b
  // separates it from the brand bar directly below it.
  return (
    <div
      role="status"
      data-testid="unsynced-capture-banner"
      className="flex items-center gap-2 border-b border-amber-300 bg-amber-50 px-4 py-1.5 text-xs font-medium text-amber-900 sm:text-sm"
    >
      <CloudOff className="h-4 w-4 shrink-0 text-amber-600" aria-hidden="true" />
      <span>
        {count} {noun} not yet saved on the server.
      </span>
    </div>
  )
}
